/** Conservative cross-browser canvas limits (Chrome/Firefox ~16384/side; Safari area ~268M px). */
export const MAX_CANVAS_DIMENSION = 16_384;
export const MAX_CANVAS_AREA = 268_435_456;
/** Floor so we never allocate a zero-size canvas. */
export const MIN_RENDER_SCALE = 0.25;

/**
 * Soft pixel budgets for pdf.js rasters.
 *
 * Layout zoom is CSS (`wrapper width = zoom * 100%`); the canvas bitmap only
 * needs enough pixels for the current interaction. Re-rasterizing a 67"-wide
 * packaging page at `1.5 * zoom` on every trackpad tick produced 30–135MP
 * canvases, 0.5–1.5s main-thread freezes, and blank frames when renders
 * cancelled mid-zoom.
 *
 * - interactive: keep pan/zoom responsive (prior frame stays painted)
 * - settled: sharper after zoom idle, still below browser hard limits
 */
export const TARGET_INTERACTIVE_PIXELS = 8_000_000;
export const TARGET_SETTLED_PIXELS = 24_000_000;

export type PdfRenderQuality = "interactive" | "settled";

/**
 * Clamp pdf.js render scale so the resulting canvas stays within browser limits.
 * Layout zoom (wrapper width) is independent — callers may CSS-upscale when clamped.
 *
 * @param baseViewportWidth - from `page.getViewport({ scale: 1, rotation })`
 * @param baseViewportHeight - same; rotation already reflected in dimensions
 * @param desiredScale - typically `1.5 * zoom`
 */
export function clampPdfRenderScale(
  baseViewportWidth: number,
  baseViewportHeight: number,
  desiredScale: number,
): number {
  if (baseViewportWidth <= 0 || baseViewportHeight <= 0) {
    return MIN_RENDER_SCALE;
  }

  const maxByWidth = MAX_CANVAS_DIMENSION / baseViewportWidth;
  const maxByHeight = MAX_CANVAS_DIMENSION / baseViewportHeight;
  const maxByArea = Math.sqrt(
    MAX_CANVAS_AREA / (baseViewportWidth * baseViewportHeight),
  );
  const maxSafe = Math.min(maxByWidth, maxByHeight, maxByArea);

  return Math.max(MIN_RENDER_SCALE, Math.min(desiredScale, maxSafe));
}

/**
 * Resolve the pdf.js scale for a given quality tier.
 * Always respects browser hard limits; additionally caps pixel count so
 * large-format pages stay interactive under CSS zoom.
 */
export function resolvePdfRenderScale(
  baseViewportWidth: number,
  baseViewportHeight: number,
  desiredScale: number,
  quality: PdfRenderQuality = "settled",
): number {
  const hardCapped = clampPdfRenderScale(
    baseViewportWidth,
    baseViewportHeight,
    desiredScale,
  );
  if (baseViewportWidth <= 0 || baseViewportHeight <= 0) {
    return hardCapped;
  }

  const targetPixels =
    quality === "interactive"
      ? TARGET_INTERACTIVE_PIXELS
      : TARGET_SETTLED_PIXELS;
  const pageArea = baseViewportWidth * baseViewportHeight;
  const maxByBudget = Math.sqrt(targetPixels / pageArea);

  return Math.max(
    MIN_RENDER_SCALE,
    Math.min(hardCapped, maxByBudget, desiredScale),
  );
}
